/*!

=========================================================
* Material Dashboard React - v1.9.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/material-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// @mui/icons-material
import AdminUserManagement from '@/views/Admin/AdminUserManagement/AdminUserManagement';
import RoleManagement from '@/views/Admin/RoleManagement/RoleManagement';
import RoleDetails from '@/views/Admin/RoleDetails';
import CustomPaymentTypes from '@/views/Admin/CustomPayments/CustomPaymentTypes';
import CreateOrEditCustomPaymentType from '@/views/Admin/CustomPayments/CreateOrEditCustomPaymentType';
import CheckAdminPermissionsComponent, {
  checkAdminPermission,
} from '@/views/Admin/CheckAdminPermissions';
import FacilityTransaction from '@/views/Clients/FacilityTransaction';
import Transactions from '@/views/Transactions/Transactions';
import TransactionDetails from '@/views/Transactions/TransactionDetails';
import ACHFiles from '@/views/Dashboard/ACHFiles';
import AchPayments from '@/views/Dashboard/Achpayments';
// core components/views for Admin layout
import DashboardPage from '@/views/Dashboard/Dashboard';
import PostageAccounting from '@/views/Dashboard/PostageAccounting';
import UploadedFiles from '@/views/Dashboard/UploadedFiles';
import AddMoovPaymentMethod from '@/views/Facilities/AddMoovPaymentMethod';
import Approvals from '@/views/Facilities/Approvals';
import CreateFacility from '@/views/Facilities/CreateFacility';
import CreateMoovAccount from '@/views/Facilities/CreateMoovAccount';
import CreateOrUpdateSmartPayablesAccount from '@/views/Facilities/CreateOrUpdateSmartPayablesAccount';
import CreateTangoAccount from '@/views/Facilities/CreateTangoAccount';
import CreateTangoGroup from '@/views/Facilities/CreateTangoGroup';
import FacilitiesPage from '@/views/Facilities/FacilitiesPage';
import MoovPage from '@/views/Facilities/MoovPage';
import SmartPayablesPage from '@/views/Facilities/SmartPayablesPage';
import TangoPage from '@/views/Facilities/TangoPage';
import UpdateFacility from '@/views/Facilities/UpdateFacility';
import UserManagement from '@/views/Facilities/UserManagement/UserManagement';
import UserDetails from '@/views/Facilities/UserDetails';
import AdminReport from '@/views/Managefile/AdminReporting';
import CreateOrganization from '@/views/Organizations/CreateOrganization';
import Organizations from '@/views/Organizations/Organizations';
import UpdateOrganization from '@/views/Organizations/UpdateOrganization';
import ClientBankAccountDetails from '@/views/Organizations/ClientBankAccountDetails';
import SftpConnectionDetails from '@/views/Organizations/SftpConnectionDetails';
import FacilityGroupDetails from '@/views/Organizations/FacilityGroups/FacilityGroupDetails';
import FacilitySettings from '@/views/Settings/FacilitySettings';
import GlobalSettings from '@/views/Settings/GlobalSettings';
import ManagedAccount from '@/views/ManagedAccounts/ManagedAccount';
import ActivateUser from '@/views/UserProfile/ActivateUser';
import ForgotPassword from '@/views/UserProfile/ForgotPassword';
import Login from '@/views/UserProfile/Login';
import Loginfacility from '@/views/UserProfile/Loginfacility';
import Resetpassword from '@/views/UserProfile/Resetpassword';
import {
  Assessment,
  CloudDownloadRounded,
  MonetizationOn,
  PersonAdd,
  ReportProblemRounded,
  AccountBalanceOutlined,
  Security,
} from '@mui/icons-material';
import Business from '@mui/icons-material/Business';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Dashboard from '@mui/icons-material/Dashboard';
import Language from '@mui/icons-material/Language';
import Payment from '@mui/icons-material/Payment';
import Person from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import Exceptions from '@/views/Facilities/Exceptions';
import SmartPayablesManagement from '@/views/Facilities/SmartPayablesManagement';

const dashboardRoutes = [
  {
    path: '/dashboard',
    name: 'Dashboard',
    translationKey: 'sidebar.dashboard',
    rtlName: 'قائمة الجدول',
    icon: Dashboard,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='dashboard'>
        <DashboardPage />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'dashboard',
  },
  {
    path: '/accounting',
    name: 'Accounting',
    translationKey: 'sidebar.accounting',
    rtlName: 'قائمة الجدول',
    icon: Assessment,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='accounting'>
        <PostageAccounting />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'accounting',
  },
  {
    path: '/adminUserManagement',
    name: 'Manage Admin Users',
    translationKey: 'sidebar.manageAdminUsers',
    rtlName: 'قائمة الجدول',
    icon: PersonAdd,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='admin_user_management'>
        <AdminUserManagement />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'admin_user_management',
  },
  {
    path: '/roles',
    name: 'Role Management',
    rtlName: 'إدارة الأدوار',
    icon: Security,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='role_management'>
        <RoleManagement />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'role_management',
  },
  {
    path: '/roles/:roleId',
    name: 'Role Details',
    rtlName: 'تفاصيل الدور',
    icon: Security,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='role_management'>
        <RoleDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'role_management',
    invisible: true,
  },
  {
    path: '/custom-payment-types',
    name: 'Custom Payment Types',
    rtlName: 'المدفوعات المخصصة',
    icon: Payment,
    component: CustomPaymentTypes,
    layout: '/admin',
    invisible: true,
  },
  {
    path: '/custom-payment-types/create',
    name: 'Create Custom Payment Type',
    rtlName: 'إنشاء دفع مخصص',
    icon: Payment,
    component: CreateOrEditCustomPaymentType,
    layout: '/admin',
    invisible: true,
  },
  {
    path: '/custom-payment-types/:id/edit',
    name: 'Edit Custom Payment Type',
    rtlName: 'تحرير الدفع المخصص',
    icon: Payment,
    component: CreateOrEditCustomPaymentType,
    layout: '/admin',
    invisible: true,
  },
  {
    path: '/organizations',
    name: 'Organizations',
    translationKey: 'sidebar.organizations',
    rtlName: '',
    icon: Business,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <Organizations />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
  },
  {
    path: '/facilities',
    name: 'Facilities',
    translationKey: 'sidebar.facilities',
    rtlName: 'ملف تعريفي للمستخدم',
    icon: Business,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facilities'>
        <FacilitiesPage />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facilities',
  },

  {
    path: '/usermanagement',
    name: 'Manage Facility Users',
    translationKey: 'sidebar.manageFacilityUsers',
    rtlName: 'قائمة الجدول',
    icon: PersonAdd,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facility_user_management'>
        <UserManagement />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facility_user_management',
  },
  {
    path: '/usermanagement/:userId',
    name: 'User Details',
    rtlName: 'تفاصيل المستخدم',
    icon: PersonAdd,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facility_user_management'>
        <UserDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facility_user_management',
    invisible: true,
  },
  {
    path: '/TangoPage',
    name: 'Manage Tango',
    translationKey: 'sidebar.manageTango',
    rtlName: 'قائمة الجدول',
    icon: CardGiftcardIcon,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='tango_management'>
        <TangoPage />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'tango_management',
  },
  {
    path: '/MoovPage',
    name: 'Manage Moov',
    translationKey: 'sidebar.manageMoov',
    rtlName: 'قائمة الجدول',
    icon: MonetizationOn,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='moov_management'>
        <MoovPage />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'moov_management',
  },
  {
    path: '/checkManagement',
    name: 'Check Management',
    translationKey: 'sidebar.checkManagement',
    rtlName: 'قائمة الجدول',
    icon: AccountBalanceOutlined,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='check_management'>
        <SmartPayablesPage />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'check_management',
  },
  {
    path: '/checkManagement/create',
    name: 'Create SmartPayables Account',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='check_management'>
        <CreateOrUpdateSmartPayablesAccount />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'check_management',
    invisible: true,
  },
  {
    path: '/checkManagement/edit/:id',
    name: 'Edit SmartPayables Account',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='check_management'>
        <CreateOrUpdateSmartPayablesAccount />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'check_management',
    invisible: true,
  },
  {
    path: '/checkManagement/management',
    name: 'SmartPayables Management',
    icon: AccountBalanceOutlined,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='check_management'>
        <SmartPayablesManagement />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'check_management',
    invisible: true,
  },
  // {
  //   path: '/refunds',
  //   name: 'Refunds',
  //   rtlName: 'ملف تعريفي للمستخدم',
  //   icon: Person,
  //   component: Refunds,
  //   layout: '/admin',
  // },
  {
    path: '/ach',
    name: 'ACH / Check Transactions',
    translationKey: 'sidebar.achCheckTransactions',
    rtlName: 'ملف تعريفي للمستخدم',
    icon: Person,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='ach_check_transactions'>
        <AchPayments />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'ach_check_transactions',
  },
  {
    path: '/transfers',
    name: 'Transactions',
    translationKey: 'sidebar.transactions',
    rtlName: 'التحويلات',
    icon: MonetizationOn,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='transactions'>
        <Transactions />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'transactions',
  },
  {
    path: '/transfers/:transferId',
    name: 'Transfer Details',
    rtlName: 'تفاصيل التحويل',
    icon: MonetizationOn,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='transactions'>
        <TransactionDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'transactions',
    invisible: true,
  },
  {
    path: '/transaction',
    name: 'Payments',
    translationKey: 'sidebar.payments',
    rtlName: 'ملف تعريفي للمستخدم',
    icon: Payment,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='payments'>
        <FacilityTransaction role='admin' />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'payments',
  },
  {
    path: '/uploadedrefundfile',
    name: 'Refund Files',
    translationKey: 'sidebar.refundFiles',
    rtlName: 'قائمة الجدول',
    icon: CloudUploadIcon,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='refund_files'>
        <UploadedFiles />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'refund_files',
  },
  {
    path: '/allachFiles',
    name: 'Ach / Positive Pay Files',
    translationKey: 'sidebar.achPositivePayFiles',
    rtlName: 'قائمة الجدول',
    icon: CloudUploadIcon,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='ach_positive_pay_files'>
        <ACHFiles />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'ach_positive_pay_files',
  },
  {
    path: '/reports',
    name: 'Reports',
    translationKey: 'sidebar.reports',
    rtlName: 'قائمة الجدول',
    icon: CloudDownloadRounded,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='reports'>
        <AdminReport />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'reports',
  },
  {
    path: '/createFacility',
    name: '',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facilities'>
        <CreateFacility />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facilities',
    invisible: true,
  },

  {
    path: '/updatefacility/:id',
    name: 'Update Facility',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facilities'>
        <UpdateFacility />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facilities',
    invisible: true,
  },
  {
    path: '/CreateMoovGroup',
    name: 'Update Charity',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='moov_management'>
        <CreateMoovAccount />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'moov_management',
    invisible: true,
  },
  {
    path: '/AddMoovPaymentMethod',
    name: 'Update Charity',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='moov_management'>
        <AddMoovPaymentMethod />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'moov_management',
    invisible: true,
  },
  {
    path: '/CreateTangoGroup',
    name: 'Update Charity',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='tango_management'>
        <CreateTangoGroup />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'tango_management',
    invisible: true,
  },
  {
    path: '/CreateTangoAccount',
    name: 'Update Charity',
    rtlName: 'قائمة الجدول',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='tango_management'>
        <CreateTangoAccount />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'tango_management',
    invisible: true,
  },

  {
    path: '/login',
    name: 'Login',
    rtlName: 'پشتیبانی از راست به چپ',
    icon: Language,
    component: Login,
    layout: '/user',
    invisible: true,
  },

  {
    path: '/global-settings',
    name: 'Global Settings',
    translationKey: 'sidebar.globalSettings',
    rtlName: 'إعدادات عامة',
    icon: SettingsIcon,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='global_settings'>
        <GlobalSettings />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'global_settings',
  },
  {
    path: '/managed-account',
    name: 'Managed Accounts',
    translationKey: 'sidebar.managedAccount',
    rtlName: 'الحساب المدار',
    icon: AccountBalanceOutlined,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='managed_accounts'>
        <ManagedAccount />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'managed_accounts',
  },
  {
    path: '/facility-settings/:id',
    name: 'Facility Settings',
    rtlName: 'إعدادات المنشأة',
    icon: SettingsIcon,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='facilities'>
        <FacilitySettings />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'facilities',
    invisible: true,
  },
  {
    path: '/facilitylogin',
    name: 'Facility Login',
    rtlName: 'پشتیبانی از راست به چپ',
    icon: Language,
    component: Loginfacility,
    layout: '/user',
    invisible: true,
  },

  {
    path: '/forgotpassword',
    name: 'Forgot Password',
    rtlName: 'پشتیبانی از راست به چپ',
    icon: Language,
    component: ForgotPassword,
    layout: '/user',
    invisible: true,
  },
  {
    path: '/resetpassword/',
    name: 'Reset Password',
    rtlName: 'پشتیبانی از راست به چپ',
    icon: Language,
    component: Resetpassword,
    layout: '/user',
    invisible: true,
  },
  {
    path: '/activation/:encoded',
    name: 'User activation',
    rtlName: 'پشتیبانی از راست به چپ',
    icon: Language,
    component: ActivateUser,
    layout: '/user',
    invisible: true,
  },
  {
    path: '/createOrganization',
    name: '',
    rtlName: '',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <CreateOrganization />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
    invisible: true,
  },
  {
    path: '/updateOrganization/:id',
    name: '',
    rtlName: '',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <UpdateOrganization />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
    invisible: true,
  },
  {
    path: '/organizations/:id/bank-accounts/:accountId',
    name: 'Client Bank Account Details',
    rtlName: '',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <ClientBankAccountDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
    invisible: true,
  },
  {
    path: '/organizations/:id/sftp-connections/:connectionId',
    name: 'SFTP Connection Details',
    rtlName: '',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <SftpConnectionDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
    invisible: true,
  },
  {
    path: '/updateOrganization/:id/facility-groups/:groupId',
    name: 'Facility Group Details',
    rtlName: '',
    icon: 'content_paste',
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='organizations'>
        <FacilityGroupDetails />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'organizations',
    invisible: true,
  },
  {
    path: '/approvals',
    name: 'Approvals',
    translationKey: 'sidebar.approvals',
    rtlName: 'الموافقات',
    icon: PersonAdd,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='approvals'>
        <Approvals role='admin' />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'approvals',
  },
  {
    path: '/exceptions',
    name: 'Resolution Center',
    translationKey: 'sidebar.resolutionCenter',
    icon: ReportProblemRounded,
    component: () => (
      <CheckAdminPermissionsComponent permissionKey='resolution_center'>
        <Exceptions role='admin' />
      </CheckAdminPermissionsComponent>
    ),
    layout: '/admin',
    permissionKey: 'resolution_center',
  },

  /* {
     path: "/typography",
     name: "Typography",
     rtlName: "طباعة",
     icon: LibraryBooks,
     component: Typography,
     layout: "/admin"
   },
   {
     path: "/icons",
     name: "Icons",
     rtlName: "الرموز",
     icon: BubbleChart,
     component: Icons,
     layout: "/admin"
   },
   {
     path: "/maps",
     icon: Notifications,
     component: NotificationsPage,
     layout: "/admin"
   },
   {
     path: "/rtl-page",
     name: "RTL Support",
     rtlName: "پشتیبانی از راست به چپ",
     icon: Language,
     component: RTLPage,
     layout: "/rtl"
   }*/
];

export const routes = dashboardRoutes;

/**
 * First admin page the logged in user is allowed to see, used as the landing
 * page after login when the default dashboard is not permitted.
 */
export const getFirstAllowedAdminPath = () => {
  const allowedRoute = dashboardRoutes.find(
    (route) =>
      route.layout === '/admin' &&
      !route.invisible &&
      route.permissionKey &&
      checkAdminPermission({ permissionKey: route.permissionKey }),
  );

  return allowedRoute ? `/admin${allowedRoute.path}` : null;
};
